'use client'

import { useRef } from 'react'
import { useInView, useScrollProgress } from '@/hooks/useInView'
import { Search, FileText, Calendar, DollarSign, ArrowRight } from 'lucide-react'

const STEPS = [
  {
    icon: Search,
    step: '01',
    title: 'Capture Leads',
    description:
      'Every call, form, referral, and walk-in lands in one pipeline. Sierra AI qualifies and follows up instantly — text, email, and call — so no lead goes cold.',
    color: 'text-blue-400',
    bg: 'bg-blue-500/10',
    glow: 'group-hover:shadow-blue-500/10',
  },
  {
    icon: FileText,
    step: '02',
    title: 'Estimate & Propose',
    description:
      'Pull satellite measurements from EagleView, auto-generate itemized estimates, and send branded proposals — all in under 5 minutes from your truck.',
    color: 'text-emerald-400',
    bg: 'bg-emerald-500/10',
    glow: 'group-hover:shadow-emerald-500/10',
  },
  {
    icon: Calendar,
    step: '03',
    title: 'Schedule & Dispatch',
    description:
      'Drag-and-drop calendar syncs with your crews in real time. Weather-aware scheduling protects your timeline. Everyone knows where to be and when.',
    color: 'text-amber-400',
    bg: 'bg-amber-500/10',
    glow: 'group-hover:shadow-amber-500/10',
  },
  {
    icon: DollarSign,
    step: '04',
    title: 'Invoice & Get Paid',
    description:
      'One-click invoicing with QuickBooks sync. Accept payments online. Track every dollar from estimate to deposit to final payment, automatically.',
    color: 'text-red-400',
    bg: 'bg-red-500/10',
    glow: 'group-hover:shadow-red-500/10',
  },
]

export function WorkflowPipeline() {
  const sectionRef = useRef<HTMLDivElement>(null)
  const { ref: inViewRef, isInView } = useInView()

  const progress = useScrollProgress(sectionRef)

  // Progress line grows as user scrolls — max 76% because the line
  // starts at left:12% and must stop at right:12% (88% - 12% = 76%)
  // Map progress [0.15, 0.45] -> [0%, 76%] with clamping
  const rawLineProgress = Math.max(0, Math.min(1, (progress - 0.15) / (0.45 - 0.15)))
  const lineWidthPercent = `${rawLineProgress * 76}%`

  return (
    <section ref={sectionRef} className="section relative overflow-hidden">
      <div ref={inViewRef} className="section-inner">
        {/* Header */}
        <div
          className={`text-center mb-12 sm:mb-16 anim-fade-in-up ${isInView ? 'in-view' : ''}`}
        >
          <p className="text-xs uppercase tracking-[0.2em] text-red-500 font-semibold mb-3">
            How It Works
          </p>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight leading-tight max-w-3xl mx-auto">
            Four Steps.{' '}
            <span className="gradient-text-red">Zero Chaos.</span>
          </h2>
          <p className="text-zinc-400 mt-4 max-w-xl mx-auto">
            From first call to final payment — one connected workflow.
          </p>
        </div>

        {/* Desktop: Horizontal pipeline with connecting line */}
        <div className="hidden lg:block relative">
          {/* Connecting line (background) */}
          <div className="absolute top-[3.5rem] left-[12%] right-[12%] h-[2px] bg-zinc-800/50 z-0" />
          {/* Animated progress line */}
          <div
            style={{ width: lineWidthPercent }}
            className="absolute top-[3.5rem] left-[12%] h-[2px] bg-gradient-to-r from-red-600 to-red-400 z-[1] transition-[width] duration-100 ease-linear"
          />

          <div className="grid grid-cols-4 gap-6 relative z-10">
            {STEPS.map((step, i) => {
              const Icon = step.icon
              return (
                <div
                  key={step.title}
                  className={`group text-center anim-fade-in-up anim-delay-${i + 2} ${isInView ? 'in-view' : ''}`}
                >
                  {/* Step node */}
                  <div className="relative mx-auto mb-6">
                    <div
                      className={`w-16 h-16 rounded-2xl ${step.bg} border border-zinc-800 flex items-center justify-center mx-auto transition-all duration-500 group-hover:scale-110 group-hover:border-zinc-700 ${step.glow} group-hover:shadow-lg`}
                    >
                      <Icon className={step.color} size={24} />
                    </div>
                    {/* Step number badge */}
                    <span className="absolute -top-2 -right-2 w-6 h-6 rounded-full bg-zinc-900 border border-zinc-700 flex items-center justify-center text-[10px] font-bold text-zinc-400">
                      {step.step}
                    </span>
                  </div>

                  {/* Content */}
                  <h3 className="text-lg font-bold text-white mb-3 group-hover:text-red-400 transition-colors">
                    {step.title}
                  </h3>
                  <p className="text-sm text-zinc-400 leading-relaxed px-2">
                    {step.description}
                  </p>
                </div>
              )
            })}
          </div>
        </div>

        {/* Mobile: Vertical pipeline */}
        <div className="lg:hidden space-y-1">
          {STEPS.map((step, i) => {
            const Icon = step.icon
            const isLast = i === STEPS.length - 1
            return (
              <div
                key={step.title}
                className={`group flex gap-4 anim-fade-in-left anim-delay-${i + 1} ${isInView ? 'in-view' : ''}`}
              >
                {/* Left rail */}
                <div className="flex flex-col items-center shrink-0">
                  <div
                    className={`w-12 h-12 rounded-xl ${step.bg} border border-zinc-800 flex items-center justify-center transition-all duration-300 group-hover:scale-110`}
                  >
                    <Icon className={step.color} size={20} />
                  </div>
                  {!isLast && (
                    <div className="w-[2px] flex-1 bg-gradient-to-b from-zinc-700 to-zinc-800/30 my-2" />
                  )}
                </div>

                {/* Content */}
                <div className="pb-8">
                  <span className="text-[10px] font-bold text-zinc-600 uppercase tracking-widest">
                    Step {step.step}
                  </span>
                  <h3 className="text-lg font-bold text-white mb-1.5 group-hover:text-red-400 transition-colors">
                    {step.title}
                  </h3>
                  <p className="text-sm text-zinc-400 leading-relaxed">
                    {step.description}
                  </p>
                </div>
              </div>
            )
          })}
        </div>
      </div>
    </section>
  )
}
